import { EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { mustShowInterestRate } from '@src/data/creditapp-selectors'
import useFeatureFlags from '@src/hooks/config-hooks'
import {
  FormatPaymentPlanForSummary,
  getIfXpressTotalObligation,
  getMerchantFees,
  getSelectedPlan,
} from '@src/data/worksheet-selectors'
import { ComputeFundingResultDto } from '@src/types/ComputeIfXpressFundingResultDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useTranslation } from 'react-i18next'

interface RowProbs {
  label: string
  value: string
}

const RowStack = ({ label, value }: RowProbs) => {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography>{label} :</Typography>
      <Typography>{value}</Typography>
    </Stack>
  )
}

interface Props {
  creditApplication: CreditApplication
  amountRequested: number
  hasVariableInterest: boolean
  firstPaymentOn: string
  merchant: Merchant
  paymentPlanId: EPaymentPlan
  computedIfXpressFunding: ComputeFundingResultDto | null
  isComputing: boolean
}
const IfXpressFundingSummary = ({
  creditApplication,
  amountRequested,
  hasVariableInterest,
  firstPaymentOn,
  merchant,
  paymentPlanId,
  computedIfXpressFunding,
  isComputing,
}: Props) => {
  const { t } = useTranslation()
  const totalObligation = getIfXpressTotalObligation(amountRequested, computedIfXpressFunding)
  const selectedPlan = getSelectedPlan(merchant, paymentPlanId)
  const { flags } = useFeatureFlags()
  const mustDisplayInterests = mustShowInterestRate(creditApplication, merchant, !!flags.UseMerchDBshowRate)
  const insuranceTotal = (computedIfXpressFunding?.insuranceFee ?? 0) + (computedIfXpressFunding?.insuranceTax ?? 0)

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        {t('worksheet.fundingSummary')} :
      </Typography>
      {isComputing && (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      {!computedIfXpressFunding && !isComputing && <Typography>{t('worksheet.personalFundingInvalid')}</Typography>}
      {computedIfXpressFunding && (
        <>
          <RowStack label={t('worksheet.requestedAmount')} value={formatCurrency(amountRequested)} />
          {mustDisplayInterests && (
            <>
              <RowStack
                label={`${t('worksheet.interest')} (${
                  hasVariableInterest
                    ? FormatPaymentPlanForSummary(selectedPlan)
                    : `${creditApplication.finalCreditDecision.interestRate}%`
                })`}
                value={formatCurrency(computedIfXpressFunding.totalInterestAmount)}
              />

              <RowStack
                label={`${t('worksheet.financeFee')} (${computedIfXpressFunding.lenderFeeRate}%)`}
                value={formatCurrency(computedIfXpressFunding.lenderFee)}
              />

              {hasVariableInterest && (
                <RowStack
                  label={`${t('worksheet.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`}
                  value={getMerchantFees(selectedPlan?.merchantFeeRate, amountRequested)}
                />
              )}

              <RowStack label={t('worksheet.protectionPlan')} value={formatCurrency(insuranceTotal)} />

              <RowStack label={t('worksheet.totalObligation')} value={formatCurrency(totalObligation)} />

              <RowStack
                label={t('worksheet.annualPercentageRate')}
                value={`${computedIfXpressFunding?.effectiveRate}%`}
              />
            </>
          )}

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('worksheet.paymentTitle')}
            </Typography>
          </Stack>

          <RowStack
            label={t('worksheet.maxPmtAmount')}
            value={formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}
          />

          <RowStack label={t('worksheet.paymentFrequency')} value={t('common.monthly')} />

          <RowStack
            label={t('worksheet.monthlyPayment')}
            value={formatCurrency(computedIfXpressFunding?.paymentForFrequency)}
          />

          <RowStack label={t('worksheet.firstPaymentOn')} value={formatDate(firstPaymentOn)} />

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <Typography fontWeight="bold">{t('worksheet.amountToBePaid')} :</Typography>
            <Typography>{formatCurrency(amountRequested ?? 0)}</Typography>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default IfXpressFundingSummary
